import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { devicesAPI, deviceToken } from "../services/api";
import common from "./messages/common";
import nav from "./messages/nav";
import auth from "./messages/auth";
import alerts from "./messages/alerts";
import pos from "./messages/pos";
import preorders from "./messages/preorders";
import purchases from "./messages/purchases";
import settings from "./messages/settings";
import dashboard from "./messages/dashboard";
import products from "./messages/products";
import devices from "./messages/devices";
import staff from "./messages/staff";
import offers from "./messages/offers";
import packs from "./messages/packs";
import ranges from "./messages/ranges";
import finance from "./messages/finance";
import mixing from "./messages/mixing";
import shop from "./messages/shop";
import credit from "./messages/credit";
import subscription from "./messages/subscription";
import journal from "./messages/journal";
import suppliers from "./messages/suppliers";
import savings from "./messages/savings";
import stock from "./messages/stock";
import reminders from "./messages/reminders";
import cash from "./messages/cash";
import help from "./messages/help";
import legal from "./messages/legal";
import signup from "./messages/signup";
import catalog from "./messages/catalog";
import modules from "./messages/modules";
import support from "./messages/support";
import onboarding from "./messages/onboarding";
import shops from "./messages/shops";
import doses from "./messages/doses";
import referrals from "./messages/referrals";

/**
 * The dashboard and the register in English, French and Arabic.
 *
 * Each area keeps its three languages side by side in one file under
 * messages/, so a sentence added in English is hard to leave out of the other
 * two. Keys read as `area.path.to.message`; `{name}` placeholders are filled
 * from the second argument, and an object of plural forms is chosen with
 * `{ count }` using the language's own plural rules (Arabic has six).
 *
 * The choice is per device: a counter tablet and the owner's phone may well
 * want different languages. Until a device picks one it follows the shop's
 * default language, then the browser's.
 */
export const LANGUAGES = [
  { code: "en", label: "English", locale: "en-GB", dir: "ltr" },
  { code: "fr", label: "Français", locale: "fr-FR", dir: "ltr" },
  // ar-TN writes digits the way Tunisian shops and receipts do: 0–9
  { code: "ar", label: "العربية", locale: "ar-TN", dir: "rtl" },
];

const AREAS = {
  common,
  nav,
  auth,
  alerts,
  pos,
  preorders,
  purchases,
  settings,
  dashboard,
  products,
  devices,
  staff,
  offers,
  packs,
  ranges,
  finance,
  mixing,
  shop,
  credit,
  subscription,
  journal,
  suppliers,
  cash,
  savings,
  stock,
  reminders,
  help,
  legal,
  signup,
  catalog,
  modules,
  onboarding,
  support,
  shops,
  doses,
  referrals,
};

const SUPPORTED = LANGUAGES.map((language) => language.code);
const STORAGE_KEY = "lang";

const languageOf = (code) =>
  LANGUAGES.find((language) => language.code === code) || LANGUAGES[0];

const DICTIONARIES = Object.fromEntries(
  SUPPORTED.map((code) => [
    code,
    Object.fromEntries(
      Object.entries(AREAS).map(([area, messages]) => [
        area,
        messages[code] || {},
      ]),
    ),
  ]),
);

const lookup = (dictionary, key) =>
  key
    .split(".")
    .reduce(
      (node, part) =>
        node && typeof node === "object" ? node[part] : undefined,
      dictionary,
    );

/** Resolves a key in one language, falling back to English, then the key. */
export const translate = (lang, key, vars = {}) => {
  let message = lookup(DICTIONARIES[lang], key);
  if (message === undefined && lang !== "en") {
    message = lookup(DICTIONARIES.en, key);
  }
  if (message === undefined) return key;

  if (typeof message === "object") {
    const count = Number(vars.count) || 0;
    const form = new Intl.PluralRules(languageOf(lang).locale).select(count);
    message = message[form] ?? message.other ?? "";
  }

  return String(message).replace(/\{(\w+)\}/g, (placeholder, name) =>
    vars[name] === undefined || vars[name] === null
      ? placeholder
      : String(vars[name]),
  );
};

const readStored = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(value) ? value : null;
  } catch {
    return null;
  }
};

const fromBrowser = () => {
  const tag = (navigator.languages?.[0] || navigator.language || "")
    .slice(0, 2)
    .toLowerCase();
  return SUPPORTED.includes(tag) ? tag : null;
};

let current = readStored() || fromBrowser() || "en";

const applyToDocument = (code) => {
  const { dir } = languageOf(code);
  document.documentElement.lang = code;
  document.documentElement.dir = dir;
};

// Before the first render, so an Arabic device never flashes left-to-right
applyToDocument(current);

/** The current language, for code that runs outside a component. */
export const getLang = () => current;
export const getLocale = () => languageOf(current).locale;

/** Translate from outside a component (helpers, contexts, error fallbacks). */
export const tr = (key, vars) => translate(current, key, vars);

const I18nContext = createContext({
  lang: "en",
  dir: "ltr",
  locale: "en-GB",
  setLang: () => {},
  t: (key, vars) => translate("en", key, vars),
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(current);
  const [chosen, setChosen] = useState(() => Boolean(readStored()));

  // Updated during render, so helpers that read getLang() while this render
  // runs already format in the new language
  current = lang;

  // No choice made on this device yet: follow the shop's default language
  useEffect(() => {
    if (chosen || !deviceToken.get()) return undefined;

    let cancelled = false;

    devicesAPI
      .current()
      .then(({ data }) => {
        const preferred = data?.shop?.default_language;
        if (!cancelled && SUPPORTED.includes(preferred)) {
          setLangState(preferred);
        }
      })
      .catch(() => {
        // Offline or not activated yet: the browser's language will do
      });

    return () => {
      cancelled = true;
    };
  }, [chosen]);

  useEffect(() => {
    applyToDocument(lang);
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!SUPPORTED.includes(code)) return;

    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // A private window just forgets the choice next time
    }

    setChosen(true);
    setLangState(code);
  }, []);

  const value = useMemo(() => {
    const { dir, locale } = languageOf(lang);

    return {
      lang,
      dir,
      locale,
      setLang,
      t: (key, vars) => translate(lang, key, vars),
    };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
